import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { templatesApi } from '@/api';
import type { QuizTemplate, PaywallTemplate, TemplatesState } from '@/types';
import { extractApiErrorWithTierNumbers } from '@/api/errorStatus';
import { rejectedApiError } from './rejectedApiError';

const initialState: TemplatesState = {
  quizTemplates: [],
  paywallTemplates: [],
  isLoading: false,
  error: null,
};

export const fetchQuizTemplatesThunk = createAsyncThunk(
  'templates/fetchQuizTemplates',
  async (filters?: { category?: string; language?: string }) =>
    templatesApi.getQuizTemplates(filters),
);

export const fetchPaywallTemplatesThunk = createAsyncThunk(
  'templates/fetchPaywallTemplates',
  async (filters?: { category?: string; language?: string }) =>
    templatesApi.getPaywallTemplates(filters),
);

/**
 * WEB-911: создание из шаблона теперь может отказать по лимиту тарифа (402
 * TIER_LIMIT). Пробрасываем статус/код/числа лимита наружу — иначе страница
 * видит голую строку и показывает техническую ошибку вместо предложения
 * перейти на платный тариф (тот же шрам, что WEB-391 на обычном создании).
 */
function preserveTierFields(error: unknown, fallbackMessage: string) {
  // WEB-1955: разбор — одной дверью. Своя сборка теряла `reasonCode`
  // (причину денежного 402) и клала `code: null` вместо «кода нет».
  return extractApiErrorWithTierNumbers(error, fallbackMessage);
}

export const createQuizFromTemplateThunk = createAsyncThunk(
  'templates/createQuizFromTemplate',
  async (
    { projectId, tid }: { projectId: string; tid: string },
    { rejectWithValue },
  ) => {
    try {
      return await templatesApi.createQuizFromTemplate(projectId, tid);
    } catch (error: unknown) {
      return rejectWithValue(
        preserveTierFields(error, 'Failed to create quiz from template'),
      );
    }
  },
);

export const createPaywallFromTemplateThunk = createAsyncThunk(
  'templates/createPaywallFromTemplate',
  async (
    { projectId, tid }: { projectId: string; tid: string },
    { rejectWithValue },
  ) => {
    try {
      return await templatesApi.createPaywallFromTemplate(projectId, tid);
    } catch (error: unknown) {
      return rejectWithValue(
        preserveTierFields(error, 'Failed to create paywall from template'),
      );
    }
  },
);

const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuizTemplatesThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQuizTemplatesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quizTemplates = action.payload as QuizTemplate[];
      })
      .addCase(fetchQuizTemplatesThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = rejectedApiError(action);
      })
      .addCase(fetchPaywallTemplatesThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPaywallTemplatesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paywallTemplates = action.payload as PaywallTemplate[];
      })
      .addCase(fetchPaywallTemplatesThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = rejectedApiError(action);
      });
  },
});

export default templatesSlice.reducer;
